import type { SlashCommandCategory, SlashCommandDefinition } from "../shared/mcp-chat-types";

// ── Category display metadata ──

export const COMMAND_CATEGORY_META: Record<SlashCommandCategory, { label: string; color: string }> = {
  chat: { label: "Chat", color: "#7c5cf5" },
  navigation: { label: "Navigate", color: "#3b82f6" },
  design: { label: "Design", color: "#22c55e" },
  ai: { label: "AI", color: "#f59e0b" },
  info: { label: "Info", color: "#6b7280" }
};

// ── Command registry ──

export const SLASH_COMMANDS: SlashCommandDefinition[] = [
  // Info
  { name: "help", label: "Help", description: "Show available commands", category: "info", mode: "immediate" },
  {
    name: "shortcuts",
    label: "Shortcuts",
    description: "Show keyboard shortcuts",
    category: "info",
    mode: "immediate"
  },
  {
    name: "context",
    label: "Show context",
    description: "Show current screen context info",
    category: "info",
    mode: "immediate"
  },
  // Chat management
  {
    name: "search",
    label: "Search",
    description: "Search messages in conversation",
    category: "chat",
    mode: "immediate"
  },
  {
    name: "clear",
    label: "Clear chat",
    description: "Clear all messages in this tab",
    category: "chat",
    mode: "immediate"
  },
  {
    name: "new",
    label: "New tab",
    description: "Open a new chat tab",
    category: "chat",
    mode: "immediate"
  },
  {
    name: "close",
    label: "Close tab",
    description: "Close the current chat tab",
    category: "chat",
    mode: "immediate"
  },
  {
    name: "rename",
    label: "Rename tab",
    description: "Rename the current tab",
    category: "chat",
    mode: "argument",
    argPlaceholder: "New tab name..."
  },
  {
    name: "export",
    label: "Export chat",
    description: "Download conversation as markdown",
    category: "chat",
    mode: "immediate"
  },
  {
    name: "designer",
    label: "Designer mode",
    description: "Switch to designer mode — UI design only, no backend changes",
    category: "chat",
    mode: "immediate"
  },
  {
    name: "builder",
    label: "Builder mode",
    description: "Switch to builder mode — full capabilities including UI design, backend, and logic",
    category: "chat",
    mode: "immediate"
  },
  {
    name: "plan",
    label: "Toggle plan mode",
    description: "Toggle plan mode on/off — AI creates a plan before making changes",
    category: "chat",
    mode: "immediate"
  },
  {
    name: "ask",
    label: "Ask mode",
    description: "Switch to ask mode — AI answers without making changes",
    category: "chat",
    mode: "immediate"
  },

  // Navigation
  {
    name: "screen",
    label: "Go to screen",
    description: "Navigate to a screen by name",
    category: "navigation",
    mode: "argument",
    argPlaceholder: "Screen name..."
  },

  // Design
  {
    name: "save",
    label: "Save screen",
    description: "Save the current screen",
    category: "design",
    mode: "immediate"
  },
  {
    name: "preview",
    label: "Preview project",
    description: "Build and open project preview",
    category: "design",
    mode: "immediate"
  },
  {
    name: "undo",
    label: "Revert AI changes",
    description: "Revert the most recent AI modifications",
    category: "design",
    mode: "immediate"
  },
  {
    name: "validate",
    label: "Validate screen",
    description: "Check for binding errors and structural issues",
    category: "design",
    mode: "immediate"
  },

  // AI
  {
    name: "review",
    label: "Review design",
    description: "Ask AI to review the current screen design",
    category: "ai",
    mode: "immediate"
  },
  {
    name: "analyze",
    label: "Analyze project",
    description: "Summarize every screen so the AI understands the project (paid — asks first)",
    category: "ai",
    mode: "immediate"
  },
  {
    name: "docs",
    label: "Search docs",
    description: "Search Kuika platform documentation",
    category: "ai",
    mode: "argument",
    argPlaceholder: "Search query..."
  }
];

// ── Lookup helpers ──

export function findCommand(name: string): SlashCommandDefinition | undefined {
  return SLASH_COMMANDS.find((c) => c.name === name);
}

export function filterCommands(query: string, isLoading: boolean): SlashCommandDefinition[] {
  const q = query.toLowerCase().trim();

  let results: SlashCommandDefinition[];
  if (!q) {
    results = SLASH_COMMANDS;
  } else {
    results = SLASH_COMMANDS.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
    );
  }

  if (isLoading) {
    results = results.filter((c) => c.name === "help" || c.name === "shortcuts");
  }

  return results;
}

export interface ParsedSlashInput {
  commandName: string;
  args: string;
  isComplete: boolean;
}

export function parseSlashInput(input: string): ParsedSlashInput | null {
  if (!input.startsWith("/")) return null;

  const withoutSlash = input.slice(1);
  const spaceIdx = withoutSlash.indexOf(" ");

  if (spaceIdx === -1) {
    return { commandName: withoutSlash, args: "", isComplete: false };
  }

  return {
    commandName: withoutSlash.slice(0, spaceIdx),
    args: withoutSlash.slice(spaceIdx + 1),
    isComplete: true
  };
}
